import { LitElement, html, css, nothing } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { ShouldUpdate } from '../../../modules/ShouldUpdate';
import './model-item-tag-selector.js';
import './model-item-tag.js';
import '@shoelace-style/shoelace/dist/components/popup/popup.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '../../katapult-elements/katapult-icon.js';
import { DEFAULT_CATEGORY_TAG_MAP } from './DefaultTags.js';

/** @import { Ref } from 'lit/directives/ref.js' */

export class CategoryTagsIcon extends LitElement {
  static properties = {
    category: { type: String },
    tagsPath: { type: String },
    modelPath: { type: String },

    _tags: { type: Array },
    _tagMenuIsActive: { type: Boolean },
    _settingTags: { type: Boolean, attribute: false }
  };

  static styles = css`
    #tagsContainer {
      display: flex;
      gap: var(--sl-spacing-x-small);
      padding: var(--sl-spacing-x-small);
      justify-content: center;
      overflow: hidden;
    }
    #hoverContainer {
      padding: var(--sl-spacing-3x-large) var(--sl-spacing-3x-large) var(--sl-spacing-3x-large) var(--sl-spacing-small);
    }
    #selectorContainer {
      background-color: var(--sl-color-neutral-0);
      border-radius: var(--sl-border-radius-medium);
      border: 1px solid var(--sl-color-neutral-300);
      min-width: 300px;
    }
    sl-tag::part(base) {
      display: flex;
      gap: var(--sl-spacing-2x-small);
      background-color: var(--sl-color-neutral-0);
      border-color: var(--sl-color-neutral-300);
      color: var(--paper-grey-800);
    }
  `;

  render() {
    const numberOfTags = this._tags.length;
    return html`
      <sl-popup
        placement="right"
        ?active=${this._tagMenuIsActive}
        strategy="fixed"
        distance="10"
        hover-bridge
        @mouseover=${() => (this._tagMenuIsActive = true)}
        @mouseleave=${this.evaluateMouseLeave}
        @click=${(e) => e.stopPropagation()}
      >
        <sl-tag slot="anchor" style="display: flex; align-items: center; justify-content: center; cursor: default;">
          ${numberOfTags == 0
            ? html`<katapult-icon icon="new_label"></katapult-icon>`
            : html`
                <katapult-icon icon="label" size="20"></katapult-icon>
                <span style="color: var(--paper-grey-800); font-size: 9pt;">${numberOfTags}</span>
              `}
        </sl-tag>
        <div id="hoverContainer">
          <div id="selectorContainer">
            <model-item-tag-selector
              .disabled=${this._settingTags}
              .modelPath=${this.modelPath}
              .tags=${this._tags}
              mayAddTags
              @change=${this.tagSelectorChangeHandler}
              ${ref(this._tagSelectorRef)}
              look-like-paper-element
            ></model-item-tag-selector>
          </div>
        </div>
      </sl-popup>
    `;
  }

  constructor() {
    super();
    /** @type {?string} */
    this.category = null;
    /** @type {?string} */
    this.tagsPath = null;
    /** @type {string[]} */
    this._tags = [];
    /** @type {?string} */
    this.modelPath = null;
    /** @type {boolean} */
    this._tagMenuIsActive = false;
    /** @type {Object} */
    this._settingTags = false;
    this._tagSelectorRef = createRef();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._disconnectTagsListener?.();
  }

  shouldUpdate(changedProperties) {
    if (changedProperties.has('category') || changedProperties.has('tagsPath')) this.#updateTagsListener();
    return ShouldUpdate(this, changedProperties);
  }

  #updateTagsListener() {
    this._disconnectTagsListener?.();
    if (!this.category || !this.tagsPath) return;

    const tagsRef = globalThis.FirebaseWorker.ref(this.tagsPath);
    const callback = (snapshot) => {
      const hardcodedTags = DEFAULT_CATEGORY_TAG_MAP[this.category] || [];
      const tagsFromSnapshot = snapshot.val() || [];
      this._tags = [...new Set([...hardcodedTags, ...tagsFromSnapshot])].sort();
    };

    tagsRef.on('value', callback);
    this._disconnectTagsListener = () => {
      tagsRef.off('value', callback);
      this._tags = [];
      this._disconnectTagsListener = null;
    };
  }

  async tagSelectorChangeHandler(e) {
    e.stopPropagation();
    this._settingTags = true;
    const tags = e.target.tags;
    if (this.tagsPath) {
      const tagsRef = globalThis.FirebaseWorker.ref(this.tagsPath);
      await tagsRef.set(tags);
    }
    this._settingTags = false;
  }

  evaluateMouseLeave() {
    // Only hide the tag menu if the create tags dialog is not open
    const tagSelector = this._tagSelectorRef?.value;
    const createTagsDialogElement = tagSelector?._createTagsDialog?.value;
    const dialog = createTagsDialogElement?._createTagDialog?.value;
    if (dialog?.opened) return;
    this._tagMenuIsActive = false;
  }
}
customElements.define('category-tags-icon', CategoryTagsIcon);
